/* =========================
   6) src/App.js
========================= */
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import PartyMaster from './pages/PartyMaster';
import CoilInEntry from './pages/CoilInEntry';
import CoilDetail from './pages/CoilDetail';
import AuditLog from './pages/AuditLog';
import UserManagement from './pages/UserManagement';
import Programs from './pages/Programs';
import ProgramChangeRequests from './pages/ProgramChangeRequests';
import Jobs from './pages/Jobs';
import Scrap from './pages/Scrap';
import ScrapDispatch from './pages/ScrapDispatch';
import Dispatch from './pages/Dispatch';
import Dies from './pages/Dies';
import Accounts from './pages/Accounts';
import DesignSystemShowcase from './pages/DesignSystemShowcase';
import Layout from './components/Layout';
import OfflineBanner from './components/OfflineBanner';

import './App.css';

// ---------------------------------------------------------------------------
// ServerReady — pings GET /health before rendering the app.
// On a Render free-tier cold start the server can take 30-60 s to wake.
// This component shows a single "Connecting…" splash instead of per-page
// "Failed to load" toast errors that confuse users.
// ---------------------------------------------------------------------------
const RAW_BACKEND =
  process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || '';
const HEALTH_URL = RAW_BACKEND.replace(/\/+$/, '') + '/health';
const RAW_GATE_MS = Number(process.env.REACT_APP_SERVER_WARMUP_GATE_MS || 12000);
const SERVER_WARMUP_GATE_MS = Number.isFinite(RAW_GATE_MS)
  ? Math.min(Math.max(RAW_GATE_MS, 4000), 20000)
  : 12000;
const SERVER_WARMUP_HARD_CAP_MS = SERVER_WARMUP_GATE_MS + 5000;
const HEALTH_REQUEST_TIMEOUT_MS = 8000;

function ServerReady({ children }) {
  const [ready, setReady] = useState(false);
  const [dots, setDots] = useState('');
  const [fallbackOpen, setFallbackOpen] = useState(false);

  useEffect(() => {
    // If backend URL is not explicitly configured, do not block app boot.
    if (!RAW_BACKEND) {
      setReady(true);
      return;
    }

    let cancelled = false;
    let stopPolling = false;
    let retryTimer = null;
    let waitTimer = null;
    let hardCapTimer = null;

    const ping = async () => {
      if (stopPolling) return;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(HEALTH_URL, { signal: controller.signal });
        clearTimeout(timer);
        if (!cancelled && res.ok) {
          stopPolling = true;
          if (waitTimer) clearTimeout(waitTimer);
          setReady(true);
        } else if (!cancelled) {
          retry();
        }
      } catch {
        clearTimeout(timer);
        if (!cancelled) retry();
      }
    };

    const retry = () => {
      retryTimer = setTimeout(() => {
        if (!cancelled) ping();
      }, 3000);
    };

    waitTimer = setTimeout(() => {
      if (cancelled) return;
      // Avoid blocking app boot forever when backend is in prolonged cold start.
      stopPolling = true;
      setFallbackOpen(true);
      setReady(true);
    }, SERVER_WARMUP_GATE_MS);

    // Extra safety: always release UI even if timers/fetch callbacks misbehave.
    hardCapTimer = setTimeout(() => {
      if (cancelled) return;
      stopPolling = true;
      setFallbackOpen(true);
      setReady(true);
    }, SERVER_WARMUP_HARD_CAP_MS);

    ping();
    return () => {
      cancelled = true;
      stopPolling = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (waitTimer) clearTimeout(waitTimer);
      if (hardCapTimer) clearTimeout(hardCapTimer);
    };
  }, []);

  // Animated dots so the user knows something is happening
  useEffect(() => {
    if (ready) return;
    const id = setInterval(
      () => setDots((d) => (d.length >= 3 ? '' : d + '.')),
      500,
    );
    return () => clearInterval(id);
  }, [ready]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="text-lg font-semibold">Connecting to server{dots}</p>
          <p className="text-sm text-muted-foreground">
            The server is warming up — this takes up to 60 seconds on the first
            visit. Please wait.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
    </>
  );
}

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (Array.isArray(allowedRoles) && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const LegacyRoute = ({ children }) => {
  const enabled = String(process.env.REACT_APP_ENABLE_LEGACY_UI || '').toLowerCase() === 'true';
  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <ServerReady>
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <OfflineBanner />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/design-system" element={<DesignSystemShowcase />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['OWNER']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parties"
              element={
                <ProtectedRoute>
                  <PartyMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coil-in"
              element={
                <ProtectedRoute>
                  <CoilInEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coil/:coilInId/:coilId"
              element={
                <ProtectedRoute>
                  <CoilDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs"
              element={
                <ProtectedRoute>
                  <Programs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/program-changes"
              element={
                <ProtectedRoute>
                  <LegacyRoute>
                    <ProgramChangeRequests />
                  </LegacyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <LegacyRoute>
                    <Jobs />
                  </LegacyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/scrap"
              element={
                <ProtectedRoute>
                  <LegacyRoute>
                    <Scrap />
                  </LegacyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/scrap-dispatch"
              element={
                <ProtectedRoute>
                  <ScrapDispatch />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dispatch"
              element={
                <ProtectedRoute>
                  <LegacyRoute>
                    <Dispatch />
                  </LegacyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dies"
              element={
                <ProtectedRoute>
                  <LegacyRoute>
                    <Dies />
                  </LegacyRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <Accounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-log"
              element={
                <ProtectedRoute>
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ServerReady>
  );
}

export default App;
